import { useState, useEffect, useRef } from 'react';
import { PhoneSVG, TabletSVG, LaptopSVG } from './DeviceSVG';
import QRCodeLib from 'qrcode';
import './InteractiveDemo.css';

interface DemoStep {
  id: number;
  title: string;
  description: string;
  details?: string[];
}

const demoSteps: DemoStep[] = [
  {
    id: 1,
    title: 'Открыл приложение',
    description: 'Сотрудник запускает приложение на телефоне',
    details: [
      'Уникальный QR-код',
      'Постоянно обновляется',
      'Нельзя сделать скриншот'
    ]
  },
  {
    id: 2,
    title: 'Сканирование планшетом',
    description: 'Планшет считывает код с экрана телефона',
    details: [
      'Быстрое распознавание',
      'Проверка подлинности кода',
      'Определение типа события'
    ]
  },
  {
    id: 3,
    title: 'Фиксация события',
    description: 'Система записывает время и данные',
    details: [
      'Отметка прихода или ухода',
      'Сохранение на планшете',
      'Подготовка к отправке'
    ]
  },
  {
    id: 4,
    title: 'Синхронизация',
    description: 'Передача данных на сервер',
    details: [
      'Отправка через интернет',
      'Или ожидание в очереди',
      'Гарантия доставки'
    ]
  },
  {
    id: 5,
    title: 'Обновление панели',
    description: 'Событие появляется в админ-панели',
    details: [
      'Отображение в списке',
      'Детали посещения',
      'Актуальная статистика'
    ]
  }
];

export default function InteractiveDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [phoneQR, setPhoneQR] = useState<string>('');
  const demoRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastAutoPlayTime = useRef<number>(0);

  // Генерация QR-кода для телефона
  useEffect(() => {
    const generateQR = async () => {
      try {
        const data = `RECVISIT:DEMO:${Date.now()}`;
        const url = await QRCodeLib.toDataURL(data, {
          width: 200,
          margin: 0,
          errorCorrectionLevel: 'M',
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setPhoneQR(url);
      } catch (error) {
        console.error('Error generating QR:', error);
      }
    };
    generateQR();
  }, []);

  // Автозапуск при прокрутке к демо
  useEffect(() => {
    if (!demoRef.current) return;

    const startAutoPlay = () => {
      const now = Date.now();
      // Защита от слишком частых запусков (минимум 5 секунд между автозапусками)
      if (now - lastAutoPlayTime.current < 5000) {
        return;
      }

      // Запускаем только если демонстрация не идет
      if (!isPlaying) {
        lastAutoPlayTime.current = now;
        setIsPlaying(true);
        setCurrentStep(0);
      }
    };

    const checkVisibility = () => {
      if (!demoRef.current || isPlaying) return;
      
      const rect = demoRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Более мягкие условия для мобильных устройств
      const isMobile = viewportWidth < 768;
      const threshold = isMobile ? 0.1 : 0.3; // На мобильных запускаем раньше
      
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const visibleRatio = visibleHeight / Math.max(rect.height, 1);
      
      // Проверяем видимость элемента
      const isVisible = rect.top < viewportHeight * (isMobile ? 0.9 : 0.7) && 
                       rect.bottom > viewportHeight * (isMobile ? 0.1 : 0.3) &&
                       visibleRatio >= threshold;
      
      if (isVisible) {
        startAutoPlay();
      }
    };

    // Создаем observer с более мягкими настройками для мобильных
    const isMobile = window.innerWidth < 768;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Более мягкий threshold для мобильных
          const minRatio = isMobile ? 0.1 : 0.3;
          if (entry.isIntersecting && entry.intersectionRatio >= minRatio) {
            startAutoPlay();
          }
        });
      },
      { 
        threshold: isMobile ? [0, 0.1, 0.2, 0.3] : [0, 0.1, 0.3, 0.5],
        rootMargin: isMobile ? '0px 0px -50px 0px' : '0px 0px -100px 0px'
      }
    );

    observerRef.current.observe(demoRef.current);

    // Fallback: проверка через scroll события для мобильных устройств
    const handleScroll = () => {
      if (!isPlaying) {
        checkVisibility();
      }
    };

    // Проверяем при монтировании и после небольших задержек
    const timeouts = [
      setTimeout(checkVisibility, 100),
      setTimeout(checkVisibility, 500),
      setTimeout(checkVisibility, 1000)
    ];

    // Добавляем scroll listener для мобильных устройств
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      timeouts.forEach(timeout => clearTimeout(timeout));
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    };
  }, [isPlaying]);

  // Автоматическое переключение шагов
  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= demoSteps.length - 1) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 2500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying]);

  // Показ деталей для текущего шага
  useEffect(() => {
    // Details are shown automatically when step changes
  }, [currentStep, isPlaying]);


  const currentStepData = demoSteps[currentStep];

  const handleManualStart = () => {
    if (!isPlaying) {
      setIsPlaying(true);
      setCurrentStep(0);
      lastAutoPlayTime.current = Date.now();
    }
  };

  return (
    <div className="interactive-demo" ref={demoRef}>
      <div className="demo-header">
        <div className="demo-header-accent"></div>
        <h3 className="demo-title">Как это работает</h3>
        <p className="demo-subtitle">Простой процесс за 5 шагов</p>
        {!isPlaying && (
          <button 
            className="demo-play-button"
            onClick={handleManualStart}
            aria-label="Запустить демонстрацию"
          >
            ▶ Запустить демонстрацию
          </button>
        )}
      </div>

      <div className="demo-main-content">
        <div className="demo-step-info">
          <div className="step-badge">Шаг {currentStep + 1} из {demoSteps.length}</div>
          <h4 className="step-title-main">{currentStepData?.title}</h4>
          <p className="step-desc-main">{currentStepData?.description}</p>
          <div className="step-tags-wrapper">
            {currentStepData?.details && isPlaying && (
              <div className="step-tags">
                {currentStepData.details.map((detail, idx) => (
                  <span key={idx} className="step-tag">{detail}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="demo-visualization">
        <div className="demo-devices">
          <div className={`device-wrapper device-phone-wrapper ${currentStep >= 0 ? 'active' : ''}`}>
            <div className="device-container">
              <PhoneSVG className="device-svg" />
              <div className={`device-screen-overlay phone-overlay ${currentStep >= 0 ? 'visible' : ''}`}>
                {currentStep >= 0 && (
                  <div className="phone-screen-content">
                    <div className="app-header">Recvisit</div>
                    <div className="qr-display">
                      {phoneQR ? (
                        <img src={phoneQR} alt="QR Code" className="qr-image" />
                      ) : (
                        <div className="qr-loading">Загрузка...</div>
                      )}
                    </div>
                    <div className="status-text">Готов к сканированию</div>
                  </div>
                )}
              </div>
            </div>
            <div className="device-label">Смартфон сотрудника</div>
          </div>

          <div className={`connection-line ${currentStep >= 1 ? 'active' : ''}`}>
            <div className="connection-dot"></div>
          </div>

          <div className={`device-wrapper device-tablet-wrapper ${currentStep >= 1 ? 'active' : ''}`}>
            <div className="device-container">
              <TabletSVG className="device-svg" />
              <div className={`device-screen-overlay tablet-overlay ${currentStep >= 1 ? 'visible' : ''}`}>
                {currentStep >= 1 && (
                  <div className="tablet-screen-content">
                    {currentStep === 1 ? (
                      <div className="scanner-view">
                        <div className="scanner-frame"></div>
                        <div className="scan-line scanning"></div>
                      </div>
                    ) : currentStep === 2 ? (
                      <div className="success-indicator">
                        <div className="checkmark">✓</div>
                        <div className="success-text">Успешно</div>
                      </div>
                    ) : currentStep === 3 ? (
                      <div className="saving-indicator">
                        <div className="save-icon">💾</div>
                        <div className="save-text">Сохранение...</div>
                        <div className="save-progress">
                          <div className="save-progress-bar"></div>
                        </div>
                      </div>
                    ) : currentStep >= 4 ? (
                      <div className="syncing-indicator">
                        <div className="sync-icon">↻</div>
                        <div className="sync-text">Синхронизация</div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            <div className="device-label">Планшет на объекте</div>
          </div>

          <div className={`connection-curve ${currentStep >= 4 ? 'active' : ''}`}>
            <svg viewBox="0 0 140 100" className="curve-svg" preserveAspectRatio="none">
              <defs>
                <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="var(--color-primary)" stopOpacity="1" />
                  <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="1" />
                </linearGradient>
              </defs>
              <path
                className="curve-line"
                d="M 0 50 Q 40 20, 80 50 Q 110 70, 140 50"
                stroke="url(#curveGradient)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
              />
              <polygon 
                className="curve-arrow" 
                points="140,50 133,47 133,53" 
                fill="var(--color-primary)"
              />
              <circle className="data-particle data-particle-1" r="3" fill="white">
                <animateMotion
                  dur="2s"
                  repeatCount="indefinite"
                  path="M 0 50 Q 40 20, 80 50 Q 110 70, 140 50"
                />
              </circle>
              <circle className="data-particle data-particle-2" r="2.5" fill="rgba(255,255,255,0.7)">
                <animateMotion
                  dur="2s"
                  repeatCount="indefinite"
                  begin="0.3s"
                  path="M 0 50 Q 40 20, 80 50 Q 110 70, 140 50"
                />
              </circle>
              <circle className="data-particle data-particle-3" r="2" fill="rgba(255,255,255,0.5)">
                <animateMotion
                  dur="2s"
                  repeatCount="indefinite"
                  begin="0.6s"
                  path="M 0 50 Q 40 20, 80 50 Q 110 70, 140 50"
                />
              </circle>
            </svg>
          </div>

          <div className={`device-wrapper device-laptop-wrapper ${currentStep >= 4 ? 'active' : ''}`}>
            <div className="device-container">
              <LaptopSVG className="device-svg" />
              <div className={`device-screen-overlay laptop-overlay ${currentStep >= 4 ? 'visible' : ''}`}>
                {currentStep >= 4 && (
                  <div className="laptop-screen-content">
                    <div className="event-list">
                      <div className="event-item new">
                        <div className="event-icon">✓</div>
                        <div className="event-info">
                          <div className="event-name">Иван Петров</div>
                          <div className="event-time">Приход • 09:15</div>
                        </div>
                      </div>
                      <div className="event-item">
                        <div className="event-icon">✓</div>
                        <div className="event-info">
                          <div className="event-name">Петр Сидоров</div>
                          <div className="event-time">Приход • 09:07</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="device-label">Админ-панель</div>
          </div>
        </div>
      </div>
      </div>

      <div className="demo-steps">
        {demoSteps.map((step, index) => (
          <div
            key={step.id}
            className={`demo-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
          >
            <div className="step-indicator">
              {index < currentStep ? '✓' : index + 1}
            </div>
            <div className="step-content">
              <h4 className="step-title">{step.title}</h4>
              <p className="step-description">{step.description}</p>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}
